/**
 * The words on the /code pages, generated from the numbers.
 *
 * Deterministic templates, no model in the loop: the same data always reads
 * the same way. Covered by story.test.ts.
 */

import { capitalize, changeText, fmtFixed, joinList, plural } from "~/lib/format";

/** Hours in words for sentences: "19 hours", "3.2 days". */
export function hoursWords(h: number): string {
  if (h < 1) return `${Math.round(h * 60)} minutes`;
  if (h < 48)
    return `${Math.round(h)} ${Math.round(h) === 1 ? "hour" : "hours"}`;
  const d = h / 24;
  if (d < 14) return `${d.toFixed(1)} days`;
  return `${(d / 7).toFixed(1)} weeks`;
}

/** "12% more", "8% fewer", "about the same" for counts. */
export function moreFewer(pct: number): string {
  if (Math.abs(pct) < 2) return "about the same number as";
  return pct > 0
    ? `${Math.round(pct)}% more than`
    : `${Math.round(-pct)}% fewer than`;
}

/**
 * Ledger change text for the /code ledgers, from the shared `changeText`.
 * Stricter than the other pages: any move off 0% shows, and "new" has no
 * arrow. Durations and rates (`digits` or `unit` set) always read as a
 * percent; counts on a base under 10 read as the raw difference.
 */
export function ledgerChange(
  current: number | null | undefined,
  prior: number | null | undefined,
  digits = 0,
  unit = "",
): { change?: string; arrow?: string } {
  return changeText(current, prior, {
    minBaseForPct: digits === 0 && unit === "" ? 10 : 0,
    flatBand: 1,
    arrowOnNew: false,
  });
}

/* ---------------- Health ---------------- */

export interface HealthStory {
  now: string;
  prior: string;
  merged: number;
  mergedPrev: number;
  mergedChangePct: number;
  avgMergeHours: number;
  avgMergeHoursPrev: number;
  attention: { changes: number; stale: number; waiting: number; large: number };
}

export function healthHeadline(s: HealthStory): string {
  const merged = plural(s.merged, "pull request");
  if (s.mergedPrev === 0) return `${merged} merged in ${s.now}`;
  return `${merged} merged in ${s.now}, ${moreFewer(s.mergedChangePct)} ${s.prior}`;
}

export function healthDek(s: HealthStory): string {
  const parts: string[] = [];
  if (s.avgMergeHours > 0) {
    const was =
      s.avgMergeHoursPrev > 0
        ? `, ${s.avgMergeHours <= s.avgMergeHoursPrev ? "down" : "up"} from ${hoursWords(s.avgMergeHoursPrev)}`
        : "";
    parts.push(
      `The average PR took ${hoursWords(s.avgMergeHours)} from open to merge${was}.`,
    );
  }
  const a = s.attention;
  const total = a.changes + a.stale + a.waiting + a.large;
  if (total === 0) {
    parts.push("Nothing open is stale, oversized or waiting on a reviewer.");
  } else {
    const bits: string[] = [];
    if (a.stale) bits.push(`${fmtFixed(a.stale)} open more than a week`);
    if (a.waiting)
      bits.push(`${fmtFixed(a.waiting)} waiting a day or more for review`);
    if (a.changes)
      bits.push(`${fmtFixed(a.changes)} sent back with changes requested`);
    if (a.large) bits.push(`${fmtFixed(a.large)} over 500 lines`);
    parts.push(`Open now: ${joinList(bits)}.`);
  }
  return parts.join(" ");
}

/* ---------------- DORA ---------------- */

export type Band = "elite" | "high" | "medium" | "low";

export const BAND_ORDER: Band[] = ["elite", "high", "medium", "low"];

export function bandName(b: Band): string {
  return b.charAt(0).toUpperCase() + b.slice(1);
}

/** The DORA bands, best first, for the shared BandScale. */
export const DORA_BANDS = BAND_ORDER.map((b) => ({ key: b, label: bandName(b) }));

/** Marks change failure rate and recovery time as proxies in a BandScale. */
export const PROXY_FLAG = {
  label: "Proxy",
  title:
    "Derived from revert, hotfix and rollback PRs, not CI/CD or incident data. Excluded from the overall band.",
};

export function doraHeadline(perWeek: number, medianLeadHours: number): string {
  const deploys =
    perWeek >= 1
      ? `${fmtFixed(perWeek, perWeek % 1 === 0 ? 0 : 1)} deploys a week`
      : perWeek > 0
        ? `About one deploy every ${fmtFixed(Math.round(1 / perWeek))} weeks`
        : "No deploys";
  const first = deploys.charAt(0).toUpperCase() + deploys.slice(1);
  if (medianLeadHours <= 0) return first;
  return `${first}, with a median of ${hoursWords(medianLeadHours)} from PR opened to deploy`;
}

export function doraDek(overall: Band, now: string): string {
  return `Measured delivery in ${now} sits in the ${bandName(overall)} band of the DORA research. Change failure rate and recovery time below are proxies built from revert and hotfix PRs, so they stay out of that band.`;
}

/* ---------------- Pull requests ---------------- */

export function prsHeadline(total: number, state: string, now: string): string {
  const what = state === "all" ? "pull request" : `${state} pull request`;
  return `${plural(total, what)} opened in ${now}`;
}

export function prsDek(
  shown: number,
  stale: number,
  large: number,
  badTitle: number,
): string {
  if (shown === 0) return "Nothing matches this filter yet.";
  const bits: string[] = [];
  if (stale)
    bits.push(
      `${fmtFixed(stale)} ${stale === 1 ? "has" : "have"} been open more than a week`,
    );
  if (large)
    bits.push(
      `${fmtFixed(large)} ${large === 1 ? "runs" : "run"} over 500 lines`,
    );
  if (badTitle)
    bits.push(
      `${fmtFixed(badTitle)} ${badTitle === 1 ? "has a title" : "have titles"} too vague to review from`,
    );
  if (bits.length === 0)
    return `Of the ${plural(shown, "PR")} listed, none is stale, oversized or vaguely titled.`;
  return `Of the ${plural(shown, "PR")} listed, ${joinList(bits)}.`;
}

/* ---------------- Commits ---------------- */

export function commitsHeadline(
  commits: number,
  added: number,
  removed: number,
  now: string,
): string {
  return `${plural(commits, "commit")} in ${now}, adding ${fmtFixed(added)} lines and removing ${fmtFixed(removed)}`;
}

export function commitsDek(
  busiestDay: { name: string; count: number } | null,
  busiestHour: { label: string; count: number } | null,
  topAuthor: { name: string; count: number; share: number } | null,
): string {
  const parts: string[] = [];
  if (busiestDay && busiestDay.count > 0) {
    parts.push(`${busiestDay.name} was the busiest day of the week`);
  }
  if (busiestHour && busiestHour.count > 0) {
    parts.push(
      `more commits landed at ${busiestHour.label} than any other hour`,
    );
  }
  let s = parts.length ? `${capitalize(parts.join(", and "))}.` : "";
  if (topAuthor && topAuthor.count > 0) {
    s += `${s ? " " : ""}${topAuthor.name} made the most, ${plural(topAuthor.count, "commit")} or ${topAuthor.share}% of the total.`;
  }
  return s;
}

/* ---------------- Review load ---------------- */

export function reviewHeadline(
  total: number,
  reviewers: number,
  top: { name: string; share: number } | null,
  now: string,
): string {
  if (total === 0) return `No reviews recorded in ${now}`;
  if (top && top.share >= 25) {
    return `${top.name} gave ${top.share}% of the ${fmtFixed(total)} reviews in ${now}`;
  }
  return `${plural(total, "review")} from ${plural(reviewers, "reviewer")}, and no one gave more than ${top?.share ?? 0}%`;
}

export function reviewDek(lowGivers: number): string {
  if (lowGivers === 0) {
    return "Every active author is giving back at least one review for every two PRs they open.";
  }
  return `${plural(lowGivers, "active author")} ${lowGivers === 1 ? "is" : "are"} opening more than two PRs for every review they give back.`;
}

/* ---------------- Repo audit ---------------- */

export function repoHeadline(
  total: number,
  critical: number,
  attention: number,
): string {
  if (total === 0) return "No repositories audited yet";
  if (critical > 0)
    return `${fmtFixed(critical)} of ${plural(total, "repository", "repositories")} score as critical`;
  if (attention > 0)
    return `${fmtFixed(attention)} of ${plural(total, "repository", "repositories")} need attention, none critical`;
  return `All ${plural(total, "repository", "repositories")} pass the audit`;
}

export function repoDek(
  stale: number,
  offConvention: number,
  avgScore: number,
): string {
  const bits: string[] = [];
  if (stale)
    bits.push(
      `${fmtFixed(stale)} ${stale === 1 ? "hasn't" : "haven't"} had a push in 90 days`,
    );
  if (offConvention)
    bits.push(
      `${fmtFixed(offConvention)} ${offConvention === 1 ? "is" : "are"} named outside the product prefixes`,
    );
  const lead = `The average score is ${fmtFixed(avgScore)} out of 100.`;
  return bits.length ? `${lead} ${capitalize(joinList(bits))}.` : lead;
}

